export const SESSION_STATE_URL = "/api/method/vatiosite.auth.get_session_state";
export const LOGOUT_METHOD = "logout";
export const SESSION_CHECK_TIMEOUT_MS = 2500;

export class FrappeRequestError extends Error {
  constructor(message, status = 0, payload = null) {
    super(message);
    this.name = "FrappeRequestError";
    this.status = Number(status) || 0;
    this.payload = payload;
  }
}

export function normalizeSessionPayload(payload) {
  return typeof payload?.message?.authenticated === "boolean"
    ? { authenticated: payload.message.authenticated }
    : { authenticated: null };
}

export function accountViewState(authenticated) {
  if (authenticated === true) return "authenticated";
  if (authenticated === false) return "guest";
  return "pending";
}

export async function getSessionState(
  fetchImplementation = globalThis.fetch,
  timeoutMs = SESSION_CHECK_TIMEOUT_MS
) {
  if (typeof fetchImplementation !== "function") return { authenticated: null };

  const controller =
    typeof AbortController === "function" ? new AbortController() : null;
  const timeoutId = globalThis.setTimeout(
    () => controller?.abort(),
    Math.max(0, Number(timeoutMs) || 0)
  );
  try {
    const response = await fetchImplementation(SESSION_STATE_URL, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      cache: "no-store",
      signal: controller?.signal,
    });
    if (!response?.ok) return { authenticated: null };
    return normalizeSessionPayload(await response.json());
  } catch (error) {
    return { authenticated: null };
  } finally {
    globalThis.clearTimeout(timeoutId);
  }
}

export function getCsrfToken(browserWindow = globalThis.window) {
  const token = String(browserWindow?.frappe?.csrf_token || "").trim();
  return token && token !== "None" ? token : "";
}

export async function callFrappeMethod(
  method,
  args = {},
  { fetchImplementation = globalThis.fetch, csrfToken = getCsrfToken() } = {}
) {
  if (typeof fetchImplementation !== "function") {
    throw new FrappeRequestError("Network request is unavailable.");
  }

  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  if (csrfToken) headers["X-Frappe-CSRF-Token"] = csrfToken;

  let response;
  try {
    response = await fetchImplementation(
      `/api/method/${String(method || "").trim()}`,
      {
        method: "POST",
        headers,
        credentials: "same-origin",
        cache: "no-store",
        body: JSON.stringify(args || {}),
      }
    );
  } catch (error) {
    throw new FrappeRequestError("Network request failed.");
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch (error) {
    payload = null;
  }
  if (!response.ok || payload?.exc) {
    throw new FrappeRequestError(
      payload?.exception || payload?.message || "Request failed.",
      response.status,
      payload
    );
  }
  return payload || {};
}

export function isAuthenticationError(error) {
  return error?.status === 401 || error?.status === 403;
}

export function logout(options = {}) {
  return callFrappeMethod(LOGOUT_METHOD, {}, options);
}
